// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Virtuoso} from 'react-virtuoso';
import {twMerge} from 'tailwind-merge';

import {
  PanelQuickAddEvent,
  trackPanelQuickAdd,
} from '../../../services/analytics/panelQuickAddEvents';
import {useDispatch} from '../../../state/hooks';
import * as PanelBankConfigActions from '../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {useDebounceState} from '../../../util/hooks';
import {getDefaultPanelConfig} from '../../../util/panelbank';
import {AddPanel} from '../../PanelBank/types';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {AddBulkButton} from './AddBulkButton';
import {PAGE_SIZE} from './constants';
import {FoundPanelsPill} from './FoundPanelsPill';
import {useQuickAddKeys} from './hooks/useQuickAddKeys';
import {KeyTypeIcon} from './KeyTypeIcon';
import {usePanelsQuickAddContext} from './PanelsQuickAddContext';
import {
  EmptyPlaceholder,
  Scroller,
  TopBottomSpacing,
} from './virtuosoComponents';

export const PanelsQuickAdd = () => {
  const {closeQuickAdd, initialSearchStr, onBack} = usePanelsQuickAddContext();
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const isGlobalQuickAdd = !targetSectionRef;

  const {
    workspaceLayout: {sortAlphabetically},
  } = useWorkspaceSettingsContext();

  const [searchStr, dSearchStr, setSearchStr] = useDebounceState(
    initialSearchStr,
    200
  );
  useEffect(
    () => setSearchStr(initialSearchStr),
    [initialSearchStr, setSearchStr]
  );

  const trackQuickAddBound = useCallback(
    (event: Omit<PanelQuickAddEvent, 'scope'>) => {
      trackPanelQuickAdd({
        scope: isGlobalQuickAdd ? 'global' : 'section',
        isSearchActive: !!searchStr,
        ...event,
      });
    },
    [isGlobalQuickAdd, searchStr]
  );

  const {searchedKeys, numPanelsByKey, keysWithNoPanels} =
    useQuickAddKeys(dSearchStr);

  const [numKeysDisplayed, setNumKeysDisplayed] = useState(PAGE_SIZE);
  const displayedKeys = useMemo(
    () => searchedKeys.slice(0, numKeysDisplayed),
    [numKeysDisplayed, searchedKeys]
  );
  const showMoreKeys = useCallback(() => {
    // must check that there are actually more keys to display, otherwise
    // we'll be stuck in an infinite setState loop at the bottom of the list
    if (searchedKeys.length > displayedKeys.length) {
      setNumKeysDisplayed(prev => prev + PAGE_SIZE);
    }
  }, [displayedKeys.length, searchedKeys.length]);

  const dispatch = useDispatch();
  const onAddOne = useCallback(
    (key: string, spec: AddPanel['spec']) => {
      trackQuickAddBound({action: 'Add one clicked'});
      if (targetSectionRef) {
        dispatch(
          PanelBankSectionConfigActions.addPanelByConfig(
            targetSectionRef,
            getDefaultPanelConfig(key, spec),
            sortAlphabetically
          )
        );
      } else {
        dispatch(
          PanelBankConfigActions.addPanelsBySpec(
            panelBankConfigRef,
            [{key, spec}],
            sortAlphabetically
          )
        );
      }
    },
    [
      dispatch,
      panelBankConfigRef,
      sortAlphabetically,
      targetSectionRef,
      trackQuickAddBound,
    ]
  );

  return (
    <>
      <div className="border-b border-solid border-moon-250">
        {onBack != null && (
          <div className="flex items-center justify-between px-24 py-16">
            <Button variant="ghost" icon="back" onClick={onBack} />
            <Button variant="ghost" icon="close" onClick={closeQuickAdd} />
          </div>
        )}
        <div
          className={twMerge(
            'flex items-center justify-between px-24',
            onBack == null && 'py-16'
          )}>
          <h2 className="text-xl font-semibold leading-[32px]">
            Add panels from keys
          </h2>
          {onBack == null && (
            <Button variant="ghost" icon="close" onClick={closeQuickAdd} />
          )}
        </div>
        <p className="px-24 pb-16 text-moon-500">
          Easily generate panels from available keys. Don't see a specific key?
          Ensure that related runs are visualized in the workspace.
        </p>
        <div className="px-24 pb-16">
          <TextField
            icon="search"
            placeholder="Search keys with regex"
            value={searchStr}
            onChange={value => setSearchStr(value)}
            onBlur={() => trackQuickAddBound({action: 'Search box blurred'})}
          />
        </div>
      </div>
      <div className="flex h-[48px] items-center justify-between gap-16 border-b border-solid border-moon-250 bg-moon-50 px-24">
        <div className="flex items-center gap-8">
          <span className="text-sm font-semibold text-moon-500">
            {dSearchStr ? 'RESULTS' : 'KEYS'}
          </span>
          <div className="rounded bg-oblivion/[0.03] px-4 text-xs font-semibold text-moon-500">
            {searchedKeys.length.toLocaleString()}
          </div>
        </div>
        <AddBulkButton
          searchedKeys={searchedKeys}
          keysWithNoPanels={keysWithNoPanels}
          trackQuickAddBound={trackQuickAddBound}
        />
      </div>
      <Virtuoso
        components={{
          Scroller,
          EmptyPlaceholder,
          Header: TopBottomSpacing,
          Footer: TopBottomSpacing,
        }}
        data={displayedKeys}
        endReached={showMoreKeys}
        increaseViewportBy={PAGE_SIZE}
        itemContent={(idx, [key, spec]) => {
          const numPanelsGlobal = numPanelsByKey.global.get(key) ?? 0;
          const numPanelsInScope = numPanelsByKey.inScope.get(key) ?? 0;
          return (
            <div
              key={key}
              className={twMerge(
                'flex items-center justify-between gap-16 px-24 hover:bg-moon-100',
                isGlobalQuickAdd ? 'py-6' : 'py-10',
                '[&:hover_button[data-test="add-one-btn"]]:visible [&_button[data-test="add-one-btn"]]:invisible'
              )}>
              <div className="flex gap-8">
                <KeyTypeIcon
                  keyType={spec.keyType}
                  isApiAddedPanel={spec.type === 'api-added-panel'}
                />
                <div>
                  <p className="break-all">
                    {isGlobalQuickAdd &&
                    key.startsWith(spec.defaultSection + '/')
                      ? key.slice(spec.defaultSection.length + 1)
                      : key}
                  </p>
                  {isGlobalQuickAdd && (
                    <p className="break-all text-sm text-moon-500">
                      {spec.defaultSection}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-8">
                {!numPanelsGlobal && (
                  <Button
                    data-test="add-one-btn"
                    variant="secondary"
                    icon="add-new"
                    size="small"
                    onClick={() => onAddOne(key, spec)}>
                    Add
                  </Button>
                )}
                {numPanelsGlobal > 0 && (
                  <FoundPanelsPill
                    isGlobalQuickAdd={isGlobalQuickAdd}
                    numPanelsGlobal={numPanelsGlobal}
                    numPanelsInScope={numPanelsInScope}
                  />
                )}
              </div>
            </div>
          );
        }}
      />
    </>
  );
};
